import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';

import { cn } from './utils';

const chipVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden px-2 font-normal whitespace-nowrap [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        neutral: 'bg-secondary text-foreground',
        positive: 'bg-primary-quiet text-primary-quiet-foreground',
        negative: 'bg-destructive-quiet text-destructive-quiet-foreground',
        outline: 'border border-border bg-card text-foreground',
      },
      // md is a rounded rectangle (radius-sm) and sm is a full pill -- Figma
      // draws the two sizes with different corner treatments, not just a
      // scaled-down version of the same shape.
      size: {
        md: 'py-1 rounded-sm text-caption',
        sm: 'py-[3px] rounded-full text-footnote',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

// Each tone pairs a different foreground/background, so muting currentColor to one
// flat opacity clears WCAG 4.5:1 on some tones and not others (positive's quiet-green
// pair needs .85 to clear it; neutral clears at .7). Values are the minimum per tone
// that keeps a >=4.5:1 composited ratio against src/tokens/tokens.css, both themes.
const countOpacity: Record<NonNullable<VariantProps<typeof chipVariants>['tone']>, string> = {
  neutral: 'opacity-[.7]',
  positive: 'opacity-[.85]',
  negative: 'opacity-[.76]',
  outline: 'opacity-[.7]',
};

type ChipCloseProps =
  { onClose: () => void; closeLabel: string } | { onClose?: undefined; closeLabel?: undefined };

type ChipProps = Omit<React.ComponentProps<'span'>, 'onClose'> &
  VariantProps<typeof chipVariants> &
  ChipCloseProps & {
    /** Leading icon; Figma draws it before the label, sized to match the text. */
    icon?: React.ReactNode;
    /** Muted trailing count, e.g. a tally beside the label. */
    count?: React.ReactNode;
  };

function Chip({
  className,
  tone = 'neutral',
  size = 'md',
  icon,
  count,
  onClose,
  closeLabel,
  children,
  ...props
}: ChipProps) {
  return (
    <span
      data-slot="chip"
      data-tone={tone}
      data-size={size}
      className={cn(chipVariants({ tone, size }), className)}
      {...props}
    >
      {icon && (
        <span
          className={cn(
            'inline-flex shrink-0 [&>svg]:size-full',
            size === 'sm' ? 'size-3' : 'size-[13px]',
          )}
        >
          {icon}
        </span>
      )}
      {children}
      {count != null && <span className={countOpacity[tone ?? 'neutral']}>{count}</span>}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="-mr-0.5 inline-flex shrink-0 items-center justify-center rounded-full opacity-70 outline-none hover:opacity-100 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:size-3"
        >
          <XIcon />
          <span className="sr-only">{closeLabel}</span>
        </button>
      )}
    </span>
  );
}

export { Chip, chipVariants };
